import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MessageCircle, RefreshCw, CheckCircle2, XCircle, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function WhatsAppLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/whatsapp-log?limit=200");
      setItems(data);
    } catch (err) {
      console.error(err);
      setItems([]);
    } finally { setLoading(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/staff/whatsapp/test-admin", { kind: "new_reservation" });
      if (data?.ok) toast.success(`Test sent · sid ${(data.sid || "").slice(0, 10)}… · watch this log for delivery status`);
      else toast.error(data?.error || "Test send failed");
      setTimeout(load, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  useEffect(() => { load(); }, []);

  const total = items.length;
  const okCount = items.filter((i) => i.success).length;
  const failCount = total - okCount;

  return (
    <div data-testid="staff-whatsapp-log">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">WhatsApp</div>
          <h1 className="mt-2 text-4xl">Message log</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Every automated WhatsApp message the back office has attempted. Includes confirmations,
            reminders, and any manual sends. Twilio SIDs are shown for successful deliveries so you
            can look them up in your Twilio console if a guest reports non-receipt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              data-testid="wa-log-test-admin"
              onClick={sendTest}
              disabled={testing}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--forest))] text-[hsl(var(--forest))] text-sm rounded hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-50"
              title="Send a test 'new reservation' message to the configured admin number"
            >
              <Send size={13} /> {testing ? "Sending…" : "Send test to admin"}
            </button>
          )}
          <button data-testid="wa-log-refresh" onClick={load} className="p-2 rounded border border-[hsl(var(--rule))]" title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4 max-w-2xl">
        <Stat label="Total" value={total} />
        <Stat label="Delivered" value={okCount} accent="text-[hsl(var(--forest))]" />
        <Stat label="Failed" value={failCount} accent="text-[hsl(6_62%_45%)]" />
      </div>

      {loading && <div className="mt-6 text-sm text-[hsl(var(--ink-soft))]">Loading...</div>}
      {!loading && total === 0 && (
        <div data-testid="wa-log-empty" className="mt-6 p-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-[hsl(var(--ink-soft))]">
          <MessageCircle size={22} className="mx-auto text-[hsl(var(--brass))]" />
          <div className="mt-3">No WhatsApp messages sent yet.</div>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="mt-6 border border-[hsl(var(--rule))] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--bg-2))] text-left text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
              <tr>
                <th className="p-3 w-6"></th>
                <th className="p-3">To</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Preview</th>
                <th className="p-3">SID / Error</th>
                <th className="p-3">When</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i, ix) => (
                <tr key={i.sid || `${i.sent_at}-${ix}`} data-testid={`wa-log-row-${ix}`} className="border-t border-[hsl(var(--rule))] align-top">
                  <td className="p-3">
                    {i.success
                      ? <CheckCircle2 size={16} className="text-[hsl(var(--forest))]" />
                      : <XCircle size={16} className="text-[hsl(6_62%_45%)]" />}
                  </td>
                  <td className="p-3 font-mono text-xs">{i.to}</td>
                  <td className="p-3 text-[hsl(var(--ink-soft))] capitalize">{i.meta?.kind || "—"}</td>
                  <td className="p-3 max-w-xs">
                    <span className="line-clamp-2 whitespace-pre-line text-xs">{i.body}</span>
                  </td>
                  <td className="p-3 text-xs">
                    {i.success ? (
                      <span className="font-mono text-[hsl(var(--ink-soft))]">{i.sid}</span>
                    ) : (
                      <span className="text-[hsl(6_62%_45%)]">
                        {i.error_code ? `[${i.error_code}] ` : ""}{i.error}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-[hsl(var(--ink-soft))]">
                    {new Date(i.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 text-3xl font-serif ${accent || ""}`}>{value}</div>
    </div>
  );
}
